export type UserRole =
  | 'Admin'
  | 'Senior Technician'
  | 'Technician'
  | 'Accounts'
  | 'Manager'
  | 'Purchasing';

export const ALL_USER_ROLES: readonly UserRole[] = [
  'Admin',
  'Senior Technician',
  'Technician',
  'Accounts',
  'Manager',
  'Purchasing',
] as const;

export interface users {
  guid: string; // UUID from Dataverse or SM4RT
  id: string; // RepairShopr ID
  fullname: string;
  email: string;
  customerId: string;
  roles: UserRole[];
  createdAt: string;
  updatedAt: string;
  notes?: string;
  /** Locked-palette avatar colour key (e.g. "navy"). Chosen in Admin → Users. */
  avatarColor?: string;
  /**
   * 3CX extension this user answers calls on (e.g. "101"). Digits only, max 10.
   * Set in Admin → Users; used to attribute ingested call records to a
   * technician. Absent = not a phone user (or not yet mapped).
   */
  extension?: string;
  /**
   * Opt-in: DM this user an Adaptive Card summary of each call attributed to
   * them (via `extension`). Set in Admin → Users. Absent / false = no DM; the
   * call record is written and readable either way. Deliberately opt-in, so
   * the rollout is one toggle per person rather than an org-wide fan-out.
   */
  sendCallDetails?: boolean;
  /**
   * The user's Entra (Azure AD) object id. sm4rt-bot addresses their 1:1 Teams
   * chat with it, so `sendCallDetails` without this is still no DM. Immutable
   * for the life of the account — backfilled once, then set for new staff.
   * Absent = this person cannot be DM'd.
   */
  entraObjectId?: string;
}

/** A printer lane — the `(siteId, role)` pair a print job is addressed to. */
export interface UserPrinterTarget {
  siteId: string;
  role: string;
}

/**
 * The signed-in user's own UI preferences, edited on sm4rt-web's Settings page
 * and written via `PATCH /api/me/preferences` (any signed-in user, own row
 * only). Stored as one JSON blob on `cr001_users.cr001_preferences_json`; the
 * API always returns every key, filled with the defaults when unset.
 */
export interface UserPreferences {
  /** Show the developer debug panel. Default false — in every build. */
  showDebugPanel: boolean;
  /**
   * The label lane the Print page preselects when this browser has no saved
   * per-label-type choice of its own. Print page only. Null = no preference
   * (the server's site default).
   */
  defaultLabelPrinter: UserPrinterTarget | null;
}

/** Body of `PATCH /api/me/preferences` — any subset; returns the full set. */
export type UserPreferencesPatch = Partial<UserPreferences>;

export interface CurrentUser {
  email: string;
  displayName: string;
  userId: string;
  roles: UserRole[];
  /** Locked-palette avatar colour key (e.g. "navy"). */
  avatarColor?: string;
  /**
   * Optional only so a client survives an API predating preferences; the
   * current API always sends it.
   */
  preferences?: UserPreferences;
}
